import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { MaterialIcons } from "@expo/vector-icons";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import EditIcon from "@mui/icons-material/Edit";
import Alert from "@mui/material/Alert";
import { Collapse, IconButton, Snackbar } from "@mui/material";
import { isWarrantyEnabled } from "@src/utils/functions";
import moment from "moment";
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { ActivityIndicator, Clipboard, Image, StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { Button, Divider } from "react-native-elements";
import SearchBar from "react-native-elements/dist/searchbar/SearchBar-ios";
import { NavigationSwitchScreenProps } from "react-navigation";
import { RootContext } from "../../../../App";
import axiosAPI from "../../../../api/axiosAPI";
import { HOST, clientV2 } from "../../../../common";
import {
  CarCategory,
  CarDetailV3,
  InventoryTable,
  ModalV3,
  SingleSelectV3,
  TooltipV3,
  VerticalTimelineComponentV3,
} from "../../../../common/component";
import {
  CGT_CHANGE_REASONS,
  getCarCategories,
  getDisplayNameForRefurbCategory,
  getRefurbCategories,
  showAlert,
  workTypeColumn,
} from "../../../../common/helper";
import { FONT_FAMILY, THEME_COLORS, images } from "../../../../common/themes";
import { inventoryStatusFilter } from "../../../InventoryListingScreen/helper";
import JobListsForRefurbCar from "../../../JobListListing/component/JobListsForRefurbCar";
import CGTListNameOnly from "../../InventoryListingV3/CGTListNameOnly";
import WorkshopListing from "../../UpcomingInventoryListingV3/WorkshopListing";
import { GET_CHILD_WORKSHOPS } from "../../UpcomingInventoryListingV3/query";
import AddCarModal, { AddCarInputProps } from "../AddCarModal";
import CGTListing from "../CGTListing";
import { ADD_CAR_AND_ASSIGN, ASSIGN_CGT, ASSIGN_INSPECTOR_V2, SUGGEST_CGT } from "../mutations";
import { GET_ADVISORS_FOR_WORKSHOP_V3, GET_AVAILABLE_CGTS, GET_INVENTORY_LIST_V3, GET_TIMELINE } from "../query";
import AppText from "@common/component/AppText";

const workTypes = getRefurbCategories();
const category = getCarCategories();
const procCategories = [
  { value: "assured", displayName: "Assured" },
  { value: "bcm", displayName: "BCM" },
  { value: "luxury", displayName: "Luxury" },
  { value: "auction", displayName: "Auction" },
  { value: "budget", displayName: "Budget" },
];

interface InventoryListingV3 {
  handleAddCar: () => void;
}

const InventoryListingV3 = React.forwardRef<
  InventoryListingV3,
  { search: string; navigation: NavigationSwitchScreenProps["navigation"] }
>(({ search, navigation }, ref) => {
  const [assignmentModal, setAssignModal] = useState<any>(null); // will contain refurbCarType
  const [cgtSearch, setCgtSearch] = useState("");
  const [selectedCGT, setCGT] = useState(null);
  const [orderBy, setOrderBy] = useState("");
  const [filters, setFilters] = useState<any[]>([]);
  const [reason, setReason] = useState("");
  const [addCarModal, setAddCarModal] = useState(false);
  const [loading, setLoading] = useState(false);
  // const [refetching, setRefetching] = useState(false);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [retry, setRetry] = useState(false);
  const [cgt, setCgt] = useState(null);
  const [step, setStep] = useState("workshop");
  const [preI3TimelineData, setPreI3Time] = useState([]);
  const [i3TimelineData, setI3Time] = useState([]);
  const [selectedTimelineTab, setSelectedTimelineTab] = useState(1);
  const [selectedTimeline, setSelectedTimeline] = useState(null);
  const [currentRow, setCurrentRow] = useState(null);
  const [selectedWSId, setSelectedWorkshopId] = useState(null);
  const [actualWSId, setActualWorkshopId] = useState(null);
  const currentLoading = useRef(new Set());
  const { value, setValue } = React.useContext(RootContext) || {};
  const [refurbCarId, setRefurbCarId] = useState(null);
  const [cgtUnassignLoading, setCGTUnassignLoading] = useState<boolean>(false);

  useImperativeHandle(
    ref,
    () => {
      return {
        handleAddCar: () => {
          setAddCarModal(true);
        },
      };
    },
    []
  );

  const {
    data: listData,
    loading: listLoading,
    refetch: listRefetch,
    fetchMore,
  } = useQuery(GET_INVENTORY_LIST_V3, {
    client: clientV2,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  const { data: workshopData, loading: childWorkshopLoading } = useQuery(GET_CHILD_WORKSHOPS, {
    client: clientV2,
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-only",
  });

  //used to get CGT list for filter in which all workshop CGT will be included
  const [getCGTForWorkshops, { data: filterCgtData, loading: filterCgtLoading }] = useLazyQuery(
    GET_ADVISORS_FOR_WORKSHOP_V3,
    {
      client: clientV2,
      fetchPolicy: "network-only",
    }
  );

  //used to get CGT list for particular workshop without capacity data
  const [getCGTForSelectedWorkshop, { data: selectedWsCgtData, loading: selectedWsCgtLoading }] = useLazyQuery(
    GET_ADVISORS_FOR_WORKSHOP_V3,
    {
      client: clientV2,
      fetchPolicy: "network-only",
    }
  );

  //used to get CGT list for particular workshop with capacity data
  const [getListOfCgts, { data: cgtData, error: cgtError, loading: cgtLoading }] = useLazyQuery(GET_AVAILABLE_CGTS, {
    client: clientV2,
    variables: {
      assignedWorkshopId: "",
      make: "",
    },
    fetchPolicy: "network-only",
  });

  const [getTimeline, { data: timelineData, loading: timelineLoading }] = useLazyQuery(GET_TIMELINE, {
    client: clientV2,
    fetchPolicy: "network-only",
    variables: {
      refurbCarId: "",
    },
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      let preI3Local = [];
      let i3Local = [];
      if (selectedTimeline?.refurbCategory == "REFURB") {
        data?.getRefurbCarTimelines?.map((item) => {
          if (item?.journeyText?.includes("Pre")) preI3Local.push(item);
          else i3Local.push(item);
        });
        setPreI3Time(preI3Local);
        setI3Time(i3Local);
      } else {
        setI3Time([]);
        setPreI3Time(data?.getRefurbCarTimelines);
      }
    },
  });

  const [assignCGT, { loading: assignLoading }] = useMutation(ASSIGN_INSPECTOR_V2, {
    client: clientV2,
  });

  const [assignWorkshop, { loading: assignWSLoading }] = useMutation(ASSIGN_CGT, {
    client: clientV2,
    variables: {},
  });

  const [suggestCGT] = useMutation(SUGGEST_CGT, { client: clientV2 });
  const [addCarAndAssign, { loading: addingCar }] = useMutation(ADD_CAR_AND_ASSIGN, { client: clientV2 });

  const changeFilter = (key: string, value: string | string[]) => {
    // Remove previous filter keys
    const newFilters = filters.filter((obj) => obj.key !== key);

    if (!value?.length) {
      setFilters(newFilters);
    } else {
      setFilters([...newFilters, { key, value }]);
    }
  };

  const onOpenCGTListing = (row: any, ev) => {
    ev.preventDefault();
    ev.stopPropagation(); // IMPORTANT: stops event propagation to parent

    getListOfCgts({ variables: { assignedWorkshopId: row?.assignedTo?.workshop?.id, make: row?.car?.make } });
    setCGT(row?.assignedTo?.user);
    setAssignModal(row);
  };

  const onOpenWorkshopListing = (row: any, ev) => {
    ev.preventDefault();
    ev.stopPropagation(); // IMPORTANT: stops event propagation to paren

    setSelectedWorkshopId(row?.assignedTo?.workshop?.id);
    setActualWorkshopId(row?.assignedTo?.workshop?.id);
    setCGT(row?.assignedTo?.user);
    setCurrentRow(row);
  };

  const timelineClick = (ev, row) => {
    ev.preventDefault();
    ev.stopPropagation();
    setSelectedTimeline(row);
    getTimeline({ variables: { refurbCarId: row?.id } });
  };

  const loadMore = () => {
    const paginator = listData?.getInventory?.paginator;

    if ((paginator && paginator.currentPage == paginator.numPages) || listLoading || loading) return;
    setLoading(true);
    fetchMore({
      variables: {
        page: paginator.currentPage + 1,
      },
    })
      .catch((err) => {
        setRetry(true);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (workshopData?.getChildWorkshops) {
      getCGTForWorkshops({
        variables: { workshopIds: workshopData?.getChildWorkshops?.map((workshop) => workshop.id), flatList: true },
      });
    }
  }, [workshopData]);

  useEffect(() => {
    if (!!listData) listRefetch({ page: 1, filters, search, orderBy });
  }, [filters, orderBy, search]);

  useEffect(() => {
    if (value?.shouldRefetchInventory) {
      listRefetch({ page: 1, filters, search, orderBy });
    }
    setValue({ shouldRefetchInventory: false });
  }, [value?.shouldRefetchInventory]);

  const onClickSort = useCallback((key: any) => {
    setOrderBy((orderBy) => (orderBy === key ? "" : key));
  }, []);

  const copyLeadIdOnPress = (text: string) => {
    Clipboard.setString(text);
  };

  const columnData = useMemo(
    () => [
      {
        name: "Work & Lead ID",
        label: "car.objectId",
        component: ({ row }: any) => {
          return (
            <View>
              <Text>{workTypeColumn[row?.refurbCategory] || "N/A"}</Text>
              <TouchableOpacity
                style={{ flexDirection: "row", alignItems: "center" }}
                onPress={() => {
                  copyLeadIdOnPress(row?.car?.objectId ?? row?.orderId ?? "N/A");
                }}
              >
                <Text style={{ marginEnd: 5, color: THEME_COLORS.textGrey }}>{row?.car?.objectId ?? row?.orderId ?? "N/A"}</Text>
                <MaterialIcons name="content-copy" size={16} color={THEME_COLORS.purple} />
              </TouchableOpacity>
            </View>
          );
        },
        width: "12%",
      },
      {
        name: "CAR",
        component: ({ row }: any) => (
          <CarDetailV3 carObject={{ ...row.car, year: row.car?.year?.slice(2) }} fontFamily={FONT_FAMILY.primaryV3} />
        ),
        width: "13%",
      },
      {
        name: "Category",
        label: "car.procCategory",
        component: ({ row }: any) => {
          return <CarCategory procCategory={row.car?.procCategory} i3Category={row?.jobList?.inspectionStatus} />;
          // return <Text>{row?.car?.procCategory?.toUpperCase()}</Text>;
        },
        width: "8%",
      },
      {
        name: "Car creation",
        label: "createdAt",
        component: ({ row }: any) => {
          return <Text>{moment(row.createdAt).format("DD MMM 'YY HH:mmA")}</Text>;
        },
        sortable: true,
        onSort: () => {
          onClickSort("created_at");
        },
        width: "14%",
      },
      {
        name: "Workshop",
        label: "assignedTo.workshop.displayName",
        component: ({ row }: any) => {
          return (
            <View style={{ flexDirection: "row", alignItems: "center" }}>
              {(!row?.assignedTo?.user?.fullName || row?.isInspectorEditable) &&
              (!isWarrantyEnabled?.() || row?.refurbCategory !== "POST_SALE_WARRANTY") ? (
                <TouchableOpacity onPress={(ev) => onOpenWorkshopListing(row, ev)}>
                  <TooltipV3 title={row?.assignedTo?.workshop?.displayName || "Not Assigned"}>
                    <Text numberOfLines={1} style={[styles.wsText, styles.textPurple]}>
                      {row?.assignedTo?.workshop?.displayName || "Not Assigned"}
                    </Text>
                  </TooltipV3>
                </TouchableOpacity>
              ) : (
                <TooltipV3 title={row?.assignedTo?.workshop?.displayName || "Not Assigned"}>
                  <Text numberOfLines={1} style={styles.wsText}>
                    {row?.assignedTo?.workshop?.displayName || "Not Assigned"}
                  </Text>
                </TooltipV3>
              )}
              {(!row?.assignedTo?.user?.fullName || row?.isInspectorEditable) &&
              (!isWarrantyEnabled?.() || row?.refurbCategory !== "POST_SALE_WARRANTY") ? (
                <IconButton size="small" onClick={(ev) => onOpenWorkshopListing(row, ev)}>
                  <EditIcon color="primary" fontSize="small" />
                </IconButton>
              ) : null}
            </View>
          );
        },
        width: "13%",
      },
      {
        name: "CGT",
        sortable: true,
        component: ({ row }: any) => {
          const isActionLoading = currentLoading.current.has(row?.id);
          const isCGTEditable =
            row?.isInspectorEditable && (!isWarrantyEnabled?.() || row?.refurbCategory !== "POST_SALE_WARRANTY");

          return (
            <View style={{ flexDirection: "row", alignItems: "center" }}>
              {isCGTEditable ? (
                <TouchableOpacity onPress={(ev) => onOpenCGTListing(row, ev)}>
                  <Text numberOfLines={2} style={[styles.cgtText, styles.textPurple]}>
                    {row?.assignedTo?.user?.fullName || "Not Assigned"}
                  </Text>
                </TouchableOpacity>
              ) : (
                <Text numberOfLines={2} style={styles.cgtText}>
                  {row?.assignedTo?.user?.fullName || "Not Assigned"}
                </Text>
              )}
              {!isActionLoading ? (
                <>
                  {isCGTEditable && (
                    <IconButton size="small" onClick={(ev) => onOpenCGTListing(row, ev)}>
                      <EditIcon color="primary" fontSize="small" />
                    </IconButton>
                  )}

                  {row?.isSoftAssigned && row?.assignedTo?.user?.fullName && (
                    <IconButton
                      size="small"
                      onClick={(ev) => {
                        ev.preventDefault();
                        ev.stopPropagation();
                        confirmAssignmentRow(row);
                      }}
                    >
                      <CheckCircleRoundedIcon color="primary" fontSize="small" />
                    </IconButton>
                  )}
                </>
              ) : (
                <ActivityIndicator size="small" color={THEME_COLORS.purple} style={{ marginLeft: 10 }} />
              )}
            </View>
          );
        },
        onSort: (val) => {
          if (val === "asc") {
            listRefetch({ page: 1, filters, orderBy, search, extraData: ["skill_management_view"] });
          } else {
            listRefetch({ page: 1, filters, orderBy, search, extraData: null });
          }
        },
        width: "15%",
      },
      {
        name: "Status",
        align: "center",
        label: "statusDisplay[0].label",
        width: "30%",
      },
      {
        name: "",
        align: "center",
        component: ({ row }: any) => {
          return (
            <TouchableOpacity onPress={(ev) => timelineClick(ev, row)}>
              <Image source={images.timeline} style={{ width: 32, height: 32 }} />
            </TouchableOpacity>
          );
        },
        width: "12%",
      },
    ],
    [currentLoading.current]
  );

  const getValue = (key: string) => {
    return filters?.find((obj) => obj.key === key)?.value || "";
  };

  const closeAssignmentModal = () => {
    if (assignLoading) return;
    setCGT(null);
    setAssignModal(null);
    setReason("");
  };

  const onCloseWorkshopModal = () => {
    setCGT(null);
    setSelectedWorkshopId(null);
    setCurrentRow(null);
    setStep("workshop");
  };

  const onSelectWorkshop = (workshopId) => {
    setSelectedWorkshopId(workshopId);
    getCGTForSelectedWorkshop({ variables: { workshopIds: [workshopId] } });
    setStep("cgt");
  };

  const onCloseTimelineModal = () => {
    setSelectedTimeline(null);
    setSelectedTimelineTab(1);
  };

  const onTimelineTabClick = (tab) => {
    setSelectedTimelineTab(tab);
  };

  const unassignCGT = async (refurbCarId: string, workshopId: string, cgtUserId: string) => {
    setCGTUnassignLoading(true);
    try {
      const postData = {
        refurb_car_id: refurbCarId,
        workshop_id: workshopId,
        cgt_user_id: cgtUserId,
      };
      const response = await axiosAPI?.({
        url: `${HOST}/refurbcar/unassign-cgt`,
        method: "POST",
        data: postData,
        headers: {
          "Keycloak-Authorization": `Bearer ${localStorage?.getItem?.("token")}`,
          "Content-Type": "application/json",
        },
      });

      if (response?.success) {
        showAlert?.(response?.message ?? "Successfully unassigned the CGT", "success");
        listRefetch?.();
        currentLoading.current.delete(refurbCarId);
        closeAssignmentModal?.();
      } else {
        throw Error(response?.message);
      }
    } catch (e: any) {
      showAlert?.(e?.message ?? "Could not unassign the CGT", "error");
    } finally {
      setCGTUnassignLoading(false);
    }
  };

  const confirmAssignmentRow = useCallback(
    (row: any) => {
      const assignedWorkshopId = row?.assignedTo?.workshop?.id;
      const cgt = selectedCGT ?? row?.assignedTo?.user;
      const refurbCarWorkshopId = row?.workshop?.id;
      currentLoading.current.add(row?.id);

      if (selectedCGT?.id === "-1") {
        unassignCGT?.(row?.id, refurbCarWorkshopId, row?.assignedTo?.user?.id);
        return;
      }

      assignCGT({
        variables: {
          data: { refurbCarId: row?.id, workshopId: assignedWorkshopId, userId: cgt?.id, reason },
        },
      })
        .then(() => {
          return listRefetch();
        })
        .then(() => {
          currentLoading.current.delete(row?.id);
          closeAssignmentModal();
          showAlert("CGT Assigned successfully", "success");
        })
        .catch(() => {
          currentLoading.current.delete(row?.id);
        });
    },
    [assignmentModal, selectedCGT, reason, currentLoading.current]
  );

  const reAssignWorkshop = useCallback(() => {
    assignWorkshop({
      variables: { refurbCarId: currentRow?.id, assignedWorkshopId: selectedWSId, userId: selectedCGT?.id },
    })
      .then(() => {
        return listRefetch();
      })
      .then(() => {
        onCloseWorkshopModal();
        showAlert("Workshop Assigned Successfully", "success");
      })
      .catch(() => {});
  }, [selectedCGT, selectedWSId, currentRow]);

  const onSuggestCGT = useCallback(() => {
    setSuggestLoading(true);
    suggestCGT()
      .then(async (data) => {
        await listRefetch();
        return data || {};
      })
      .then(({ data }) => {
        if (data?.suggestCgt?.success) {
          showAlert("CGT suggested successfully", "success");
        } else {
          showAlert(data?.suggestCgt?.errorMessage, "error");
        }
      })
      .finally(() => {
        setSuggestLoading(false);
      });
  }, []);

  const closeRetryModal = () => {
    setRetry(false);
  };

  const onConfirmAddCar = (data: AddCarInputProps) => {
    addCarAndAssign({ variables: { data } })
      .then(() => {
        return listRefetch();
      })
      .then(() => {
        showAlert("Car added successfully", "success");
        setAddCarModal(false);
      });
  };

  const onRowClick = (row) => {
    let params: any = {};
    params.filter = JSON.stringify([...filters]);
    params.search = search;
    navigation?.navigate("CarRefurbMetrics", { refurbCarId: row?.id, parent: "InventoryListingV3.1", params });
    // setRefurbCarId(row?.id);
  };

  const cgtResults = useMemo(() => {
    const unassignCGTRow = { id: "-1", user: [{ id: "-1", fullName: "Un-assign CGT" }] };
    if (Array.isArray(cgtData?.getAvailableWorkshopCgtForMake)) {
      if (cgtSearch == "") return [...cgtData?.getAvailableWorkshopCgtForMake, unassignCGTRow];
      return [
        ...cgtData?.getAvailableWorkshopCgtForMake?.filter((cgtItem) =>
          cgtItem.user?.[0]?.fullName?.toLowerCase().includes(cgtSearch)
        ),
        unassignCGTRow,
      ];
    } else return [];
  }, [cgtSearch, cgtData?.getAvailableWorkshopCgtForMake]);

  return (
    <>
      <View style={{ flexDirection: "row", flexWrap: "wrap" }}>
        <SingleSelectV3
          options={workTypes}
          value={getValue("workType")}
          setValue={(val) => changeFilter("workType", val)}
          valueExtractor="value"
          labelExtractor="displayName"
          placeholder="Work Type"
          clearIconVisible
        />
        <SingleSelectV3
          options={procCategories}
          value={getValue("procCategory")}
          setValue={(val) => changeFilter("procCategory", val)}
          valueExtractor="value"
          labelExtractor="displayName"
          placeholder="Proc Category"
          clearIconVisible
        />
        <SingleSelectV3
          options={category}
          value={getValue("carCategory")}
          setValue={(val) => changeFilter("carCategory", val)}
          valueExtractor="value"
          labelExtractor="displayName"
          placeholder="i3 Category"
          clearIconVisible
        />
        {workshopData?.getChildWorkshops && (
          <SingleSelectV3
            options={workshopData?.getChildWorkshops || []}
            value={getValue("workshopId")}
            setValue={(val) => changeFilter("workshopId", val)}
            valueExtractor="id"
            labelExtractor="displayName"
            placeholder="Workshop"
            clearIconVisible
          />
        )}
        <SingleSelectV3
          options={filterCgtData?.getAdvisorsForWorkshop || []}
          value={getValue("advisorId")}
          setValue={(val) => changeFilter("advisorId", val)}
          valueExtractor="id"
          labelExtractor="fullName"
          placeholder="CGT"
          clearIconVisible
        />
        <SingleSelectV3
          options={inventoryStatusFilter()}
          value={getValue("status")}
          setValue={(val) => changeFilter("status", val)}
          valueExtractor="value"
          labelExtractor="displayName"
          placeholder="Status"
          clearIconVisible
        />
      </View>
      <View style={styles.divider}>
        <AppText fontSize={14} fontFamily="bold">
          Total: {listData?.getInventory?.data?.length ?? 0}
        </AppText>
        <Divider color={THEME_COLORS.sideMenuBg} style={{ flex: 1 }} width={4} />
      </View>
      <InventoryTable
        columnData={columnData}
        data={listData?.getInventory}
        loading={listLoading || loading}
        page={listData?.getInventory?.paginator?.currentPage}
        totalPages={listData?.getInventory?.paginator?.numPages || 0}
        cgtSelected={selectedCGT}
        loadMore={loadMore}
        onRowClick={onRowClick}
        rowProps={{ clickable: true }}
      />

      <ModalV3
        visible={currentRow}
        header={"Assign Workshop"}
        subheader={`${currentRow?.car?.year} ${currentRow?.car?.make} ${currentRow?.car?.model} ${
          currentRow?.car?.variant
        } • ${currentRow?.car?.registrationNo?.toUpperCase()}`}
        onCancel={onCloseWorkshopModal}
        onConfirm={() => reAssignWorkshop()}
        disabledConfirm={
          !selectedCGT || childWorkshopLoading || cgtLoading || assignWSLoading || actualWSId === selectedWSId
        }
        cancelButtonText={"Close"}
        confirmButtonText={"Re-Assign"}
      >
        {childWorkshopLoading ? (
          <View style={[styles.flexBox, { justifyContent: "center" }]}>
            <ActivityIndicator size="large" color={THEME_COLORS.purple} />
          </View>
        ) : (
          <>
            <Collapse in={step == "workshop"}>
              <View>
                <Text style={styles.modalSelectText}>Select Workshop</Text>
                <Text style={styles.modalDesc}>
                  Select the workshop at which you want the car to be treated and click on assign
                </Text>
              </View>
              <div style={{ height: "75vh" }}>
                <WorkshopListing
                  data={workshopData?.getChildWorkshops}
                  selectWorkshopId={onSelectWorkshop}
                  selected={selectedWSId}
                  actualWorkshopId={actualWSId}
                />
              </div>
            </Collapse>
            <Collapse in={!!selectedWSId && step != "workshop"}>
              <View style={{ flexDirection: "row" }}>
                <TouchableOpacity style={{ marginBottom: 16 }} onPress={() => setStep("workshop")}>
                  <Text style={[styles.modalSelectText, { color: THEME_COLORS.textGrey }]}>{`Select Workshop`}</Text>
                </TouchableOpacity>
                <Text style={[styles.modalSelectText, { marginLeft: 16 }]}>{">"}</Text>
                <Text style={[styles.modalSelectText, { marginLeft: 16 }]}>Select CGT</Text>
              </View>

              <div style={{ height: "75vh" }}>
                {cgtLoading ? (
                  <View style={[styles.flexBox, { justifyContent: "center" }]}>
                    <ActivityIndicator size="large" color={THEME_COLORS.purple} />
                  </View>
                ) : (
                  <CGTListNameOnly
                    data={selectedWsCgtData?.getAdvisorsForWorkshop}
                    selected={selectedCGT}
                    onSelect={setCGT}
                  />
                )}
              </div>
            </Collapse>
          </>
        )}
      </ModalV3>

      <ModalV3
        visible={assignmentModal}
        header={"Re-assign CGT"}
        subheader={"Please select the CGT to whom you want to re-assign"}
        onCancel={() => closeAssignmentModal()}
        onConfirm={() => confirmAssignmentRow(assignmentModal)}
        confirmButtonText="Save"
        disabledConfirm={
          !selectedCGT ||
          (reason == "" && selectedCGT?.id !== "-1") ||
          selectedCGT?.id == assignmentModal?.assignedTo?.user?.id ||
          listLoading ||
          assignLoading ||
          cgtUnassignLoading
        }
        width={"60vw"}
      >
        <View style={{ marginTop: 16 }}>
          <Text style={styles.reasonForChange}>Reason for re-assigning CGT?</Text>
          <SingleSelectV3
            options={CGT_CHANGE_REASONS}
            value={reason}
            valueExtractor="value"
            labelExtractor="label"
            setValue={(val) => setReason(val)}
            placeholder="Select Reason"
            noOutline
            style={{ marginTop: "2px", marginLeft: "2px", minWidth: reason != "" ? 300 : 150, width: "auto" }}
            clearIconVisible={false}
            markup
            containerStyle={{ minWidth: reason != "" ? 300 : 150, width: "fit-content" }}
          />
        </View>
        {/* <View style={{ opacity: reason != '' ? 1 : 0.5}} pointerEvents={reason != '' ? 'auto' : 'none' }> */}
        <SearchBar
          placeholder={"Search with workshop, CGT...."}
          onChangeText={setCgtSearch}
          value={cgtSearch}
          inputContainerStyle={styles.modalSearch}
          inputStyle={{ outline: "none", fontSize: 15 }}
          cancelButtonTitle=""
          cancelButtonProps={{ buttonStyle: { width: 8 } }}
        />

        {cgtLoading ? (
          <View style={[styles.flexBox, { justifyContent: "center" }]}>
            <ActivityIndicator size="large" color={THEME_COLORS.purple} />
          </View>
        ) : (
          <CGTListing
            data={cgtResults}
            selected={selectedCGT}
            onSelect={setCGT}
            suggested={assignmentModal?.isSoftAssigned ? assignmentModal?.assignedTo?.user : {}}
            disableAll={reason == ""}
            isCgtCapacityHidden={true}
          />
        )}
        {/* </View> */}
      </ModalV3>

      <ModalV3
        visible={selectedTimeline}
        header={"History"}
        subheader={`${selectedTimeline?.car?.registrationNo?.toUpperCase()} • ${selectedTimeline?.car?.year} ${selectedTimeline?.car?.make} ${selectedTimeline?.car?.model}`}
        onClose={() => onCloseTimelineModal()}
        width={"40vw"}
      >
        {timelineLoading ? (
          <View style={[styles.flexBox, { justifyContent: "center" }]}>
            <ActivityIndicator size="large" color={THEME_COLORS.purple} />
          </View>
        ) : (
          <View style={{ height: "95vh" }}>
            <View style={styles.timelineTabCon}>
              <TouchableOpacity
                onPress={() => onTimelineTabClick(1)}
                style={[styles.timelineTab, selectedTimelineTab != 1 && styles.unselectedTab]}
              >
                <Text style={[styles.tabHeadingText, selectedTimelineTab != 1 && styles.unselectedText]}>
                  {selectedTimeline?.refurbCategory == "REFURB"
                    ? "Pre-i3 Timeline"
                    : getDisplayNameForRefurbCategory(selectedTimeline?.refurbCategory)}
                </Text>
              </TouchableOpacity>
              {selectedTimeline?.refurbCategory == "REFURB" && (
                <TouchableOpacity
                  onPress={() => onTimelineTabClick(2)}
                  style={[styles.timelineTab, selectedTimelineTab != 2 && styles.unselectedTab]}
                >
                  <Text style={[styles.tabHeadingText, selectedTimelineTab != 2 && styles.unselectedText]}>
                    i3 Timeline
                  </Text>
                </TouchableOpacity>
              )}
            </View>
            <View style={{ marginTop: 60 }}>
              <VerticalTimelineComponentV3
                data={
                  selectedTimeline?.refurbCategory == "REFURB"
                    ? selectedTimelineTab == 1
                      ? preI3TimelineData
                      : i3TimelineData
                    : preI3TimelineData
                }
              />
            </View>
          </View>
        )}
      </ModalV3>

      <AddCarModal
        visible={addCarModal}
        onClose={() => setAddCarModal(false)}
        onAddCar={onConfirmAddCar}
        loading={addingCar || listLoading}
      />

      {refurbCarId ? <JobListsForRefurbCar refurbCarId={refurbCarId} onClose={() => setRefurbCarId(null)} /> : null}

      <Snackbar open={retry} autoHideDuration={6000} onClose={closeRetryModal}>
        <Alert severity="warning" action={<Button title="Retry" type="clear" titleStyle={styles.retryButton} />}>
          Failed to more list!
        </Alert>
      </Snackbar>
    </>
  );
});

const styles = StyleSheet.create({
  container: {
    padding: "1.2em",
  },
  inputContainer: {
    backgroundColor: THEME_COLORS.grey2,
    width: "30vw",
  },
  modalSearch: {
    backgroundColor: "transparent",
  },
  flexBox: { flexDirection: "row", alignItems: "center" },
  button: {
    paddingHorizontal: "1em",
    paddingVertical: "0.6em",
    backgroundColor: `${THEME_COLORS.purple}aa`,
    width: "fit-content",
    minWidth: 150,
  },
  buttonTitle: {
    fontSize: 14,
    fontFamily: FONT_FAMILY.mediumV3,
  },
  reasonForChange: {
    fontFamily: FONT_FAMILY.mediumV3,
  },
  retryButton: {
    color: THEME_COLORS.chartYellow,
    fontSize: 14,
  },
  addCarButton: {
    backgroundColor: THEME_COLORS.white,
    borderColor: THEME_COLORS.purple,
  },
  addCarTitle: {
    color: THEME_COLORS.purple,
  },
  modalSelectText: {
    marginTop: "1.5em",
    fontFamily: FONT_FAMILY.mediumV3,
    fontSize: 18,
  },
  modalDesc: {
    marginVertical: "0.6em",
    fontFamily: FONT_FAMILY.primaryV3,
    fontSize: 15,
    color: THEME_COLORS.sideMenuGrey,
  },
  timelineTab: {
    width: "50%",
    alignItems: "center",
    paddingVertical: 15,
    borderBottomWidth: 2,
    borderBottomColor: THEME_COLORS.purple,
  },
  unselectedTab: {
    borderBottomColor: `${THEME_COLORS.sideMenuGrey}33`,
  },
  timelineTabCon: {
    width: "100%",
    left: 0,
    position: "absolute",
    flexDirection: "row",
    marginTop: 10,
  },
  tabHeadingText: {
    fontFamily: FONT_FAMILY.primaryV3,
    fontSize: 15,
    color: THEME_COLORS.purple,
  },
  unselectedText: {
    color: `${THEME_COLORS.sideMenuGrey}80`,
  },
  cgtText: {
    maxWidth: 130,
  },
  wsText: {
    maxWidth: 100,
  },
  textPurple: {
    color: THEME_COLORS.purple,
  },
  divider: {
    // @ts-ignore
    marginTop: "1em",
    flexDirection: "row",
    alignItems: "center",
    columnGap: 16,
  },
});

export default InventoryListingV3;
